import React, { useState, useEffect } from 'react';

const GallerySection = () => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(4);

  useEffect(() => {
    fetchGallery();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setSlidesToShow(2);
      } else if (window.innerWidth < 768) {
        setSlidesToShow(3);
      } else if (window.innerWidth < 1024) {
        setSlidesToShow(4);
      } else {
        setSlidesToShow(5);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await fetch('./admin/api/gallery.php');
      const data = await response.json();
      
      if (data.success) {
        setGallery(data.data);
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };


  const openLightbox = (image) => {
    setSelectedImage(image);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const maxSlides = Math.max(0, Math.ceil(gallery.length / slidesToShow) - 1);

  const nextSlide = () => {
    setCurrentSlide(prev => Math.min(prev + 1, maxSlides));
  };

  const prevSlide = () => {
    setCurrentSlide(prev => Math.max(prev - 1, 0));
  };

  if (loading) {
    return (
      <section className="py-8 sm:py-10 lg:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12 sm:py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading gallery...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 sm:py-10 lg:py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 sm:mb-4">Photo Gallery</h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">Capturing precious moments and memories</p>
        </div>

        {/* Gallery Slider */}
        {gallery.length === 0 ? (
          <div className="text-center py-12 sm:py-16">
            <div className="bg-gray-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 max-w-sm sm:max-w-md mx-auto">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="fas fa-camera text-2xl sm:text-3xl text-gray-400"></i>
              </div>
              <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">
                No Photos Available
              </h4>
              <p className="text-sm sm:text-base text-gray-600">
                We're capturing amazing moments to share with you soon!
              </p>
            </div>
          </div>
        ) : (
          <div className="relative">
            {/* Slider Container */}
            <div className="overflow-hidden rounded-xl sm:rounded-2xl">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * (100 / slidesToShow)}%)` }}
              >
                {gallery.slice(0, 12).map((item) => (
                  <div 
                    key={item.id} 
                    className="flex-shrink-0 px-1 sm:px-2"
                    style={{ width: `${100 / slidesToShow}%` }}
                  >
                    <div className="group cursor-pointer" onClick={() => openLightbox(item)}>
                      <div className="relative overflow-hidden rounded-lg bg-gray-100 aspect-square">
                        <img 
                          src={`admin/${item.image_path}`}
                          alt={item.title}
                          className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-700 ease-out"
                          loading="lazy"
                        />
                        
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300"></div>
                        
                        {/* Zoom Icon */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="bg-white/90 backdrop-blur-sm rounded-full p-2 sm:p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                            <i className="fas fa-search-plus text-gray-800 text-sm sm:text-lg"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Arrows */}
            {gallery.length > slidesToShow && (
              <>
                <button
                  onClick={prevSlide}
                  className="absolute left-2 sm:left-4 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-full p-2 sm:p-3 shadow-lg hover:bg-white transition-all duration-300 z-10"
                  disabled={currentSlide === 0}
                >
                  <i className="fas fa-chevron-left text-gray-800 text-sm sm:text-lg"></i>
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-2 sm:right-4 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-full p-2 sm:p-3 shadow-lg hover:bg-white transition-all duration-300 z-10"
                  disabled={currentSlide >= maxSlides}
                >
                  <i className="fas fa-chevron-right text-gray-800 text-sm sm:text-lg"></i>
                </button>
              </>
            )}

            {/* Dots Indicator */}
            {gallery.length > slidesToShow && (
              <div className="flex justify-center mt-4 sm:mt-6 space-x-2">
                {Array.from({ length: maxSlides + 1 }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                      currentSlide === index 
                        ? 'bg-blue-600 scale-125' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* View Full Gallery Button */}
        {gallery.length > 0 && (
          <div className="text-center mt-8 sm:mt-12">
            <a 
              href="/gallery" 
              className="inline-flex items-center bg-blue-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold hover:bg-blue-700 transition-colors duration-200 shadow-lg hover:shadow-xl text-sm sm:text-base lg:text-lg"
            >
              <i className="fas fa-images mr-2 sm:mr-3"></i>
              View Full Gallery
            </a>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={closeLightbox}>
          <div className="bg-white rounded-2xl max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-end p-4 border-b border-gray-200">
              <button 
                onClick={closeLightbox}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2"
              >
                <i className="fas fa-times text-2xl"></i>
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-4">
              <div className="text-center">
                {/* Image */}
                <div className="relative inline-block">
                  <img 
                    src={`admin/${selectedImage.image_path}`}
                    alt={selectedImage.title}
                    className="max-w-full max-h-[70vh] rounded-xl shadow-lg object-contain"
                  />
                </div>
                
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default GallerySection;
